import { ErrorResponse } from '~/infra/ErrorResponse';
export const encodeFilterToUrl = (filter)=>JSON.stringify(filter);
const MAX_FILTER_PARAM_LENGTH = 4000;
const MAX_FILTER_DEPTH = 12;
const PREDICATE_ARM_KEYS = new Set([
    'and',
    'or'
]);
const SCALAR_LIST_KEYS = new Set([
    'in',
    'nin'
]);
const NESTED_PREDICATE_KEYS = new Set([
    'not',
    'any',
    'all',
    'none'
]);
const isPlainObject = (value)=>typeof value === 'object' && value !== null && !Array.isArray(value);
const isScalar = (value)=>value === null || typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
const isScalarList = (value)=>Array.isArray(value) && value.every(isScalar);
const isSearchFilterPredicate = (node, depth)=>{
    if (depth > MAX_FILTER_DEPTH || !isPlainObject(node)) return false;
    return Object.entries(node).every((param)=>{
        let [key, value] = param;
        if (PREDICATE_ARM_KEYS.has(key)) return Array.isArray(value) && value.every((arm)=>isSearchFilterPredicate(arm, depth + 1));
        if (SCALAR_LIST_KEYS.has(key)) return isScalarList(value);
        if (NESTED_PREDICATE_KEYS.has(key)) return isSearchFilterPredicate(value, depth + 1);
        if (isPlainObject(value)) return isSearchFilterPredicate(value, depth + 1);
        return isScalar(value) || isScalarList(value);
    });
};
export const readFilterFromUrl = (param)=>{
    if (!param || param.length > MAX_FILTER_PARAM_LENGTH) return null;
    try {
        const parsed = JSON.parse(param);
        if (!isSearchFilterPredicate(parsed, 0) || parsed.version !== '1') return null;
        return parsed;
    } catch (unused) {
        return null;
    }
};
export const readSelectedDraftIdsFromLocationState = (state)=>{
    if (state == null || typeof state !== 'object') return [];
    const value = state.selectedDraftIds;
    if (!Array.isArray(value)) return [];
    return value.filter((id)=>typeof id === 'string');
};
export const selectedRowsFromDraftIds = (ids)=>ids.reduce((acc, id)=>Object.assign(acc, {
            [id]: true
        }), {});
export const draftDashboardTextsPath = 'pages.draftsDashboard.editTable';
export const getUICompanyName = (draft, anchorSampleLabel)=>{
    return draft.isSample ? anchorSampleLabel : draft.contactDetails ? draft.contactDetails.companyName : '';
};
function getDraftDisplayLabel(draft, anchorSampleLabel, unnamedLabel) {
    const companyName = getUICompanyName(draft, anchorSampleLabel);
    if (companyName) return draft.title ? "".concat(companyName, " (").concat(draft.title, ")") : companyName;
    return draft.title || unnamedLabel;
}
export function selectedDraftNames(selectedDraftIds, draftsMap, anchorSampleLabel, unnamedLabel) {
    return selectedDraftIds.map((id)=>draftsMap.get(id)).filter((draft)=>draft != null).map((draft)=>getDraftDisplayLabel(draft, anchorSampleLabel, unnamedLabel));
}
export function selectedDraftNamesById(selectedDraftIds, draftsMap, anchorSampleLabel, unnamedLabel) {
    return Object.fromEntries(selectedDraftIds.map((id)=>[
            id,
            draftsMap.get(id)
        ]).filter((entry)=>entry[1] != null).map((param)=>{
        let [id, draft] = param;
        return [
            id,
            getDraftDisplayLabel(draft, anchorSampleLabel, unnamedLabel)
        ];
    }));
}
const sortById = (a, b)=>a.id < b.id ? -1 : 1;
const sortByCompanyName = (a, b)=>{
    var _a_contactDetails, _b_contactDetails;
    const contactCompanyNameA = ((_a_contactDetails = a.contactDetails) === null || _a_contactDetails === void 0 ? void 0 : _a_contactDetails.companyName) || '';
    const contactCompanyNameB = ((_b_contactDetails = b.contactDetails) === null || _b_contactDetails === void 0 ? void 0 : _b_contactDetails.companyName) || '';
    const noDataA = !contactCompanyNameA && !a.title;
    const noDataB = !contactCompanyNameB && !b.title;
    if (noDataA && noDataB) {
        return 0;
    }
    if (noDataA) {
        return 1;
    }
    if (noDataB) {
        return -1;
    }
    const nameA = contactCompanyNameA.toLowerCase() + (a.title ? " (".concat(a.title.toLowerCase(), ")") : '');
    const nameB = contactCompanyNameB.toLowerCase() + (b.title ? " (".concat(b.title.toLowerCase(), ")") : '');
    if (nameA < nameB) {
        return -1;
    } else if (nameA > nameB) {
        return 1;
    } else {
        return 0;
    }
};
const sortByModifiedBy = (a, b)=>{
    const modifiedByA = a.lastModifiedBy ? "".concat(a.lastModifiedBy.firstName, " ").concat(a.lastModifiedBy.lastName) : '';
    const modifiedByB = b.lastModifiedBy ? "".concat(b.lastModifiedBy.firstName, " ").concat(b.lastModifiedBy.lastName) : '';
    if (modifiedByA < modifiedByB) {
        return -1;
    } else if (modifiedByA > modifiedByB) {
        return 1;
    } else {
        return 0;
    }
};
const sortByLastModifiedOn = (a, b)=>b.lastModifiedOn - a.lastModifiedOn;
const sortFunctions = {
    0: (isAscending)=>(a, b)=>{
            const retval = sortByCompanyName(a, b) || sortByModifiedBy(a, b) || sortById(a, b);
            return isAscending ? -retval : retval;
        },
    1: (isAscending)=>(a, b)=>{
            const retval = sortByModifiedBy(a, b) || sortByCompanyName(a, b) || sortById(a, b);
            return isAscending ? -retval : retval;
        },
    2: (isAscending)=>(a, b)=>{
            const retval = sortByLastModifiedOn(a, b) || sortById(a, b);
            return isAscending ? -retval : retval;
        }
};
function textSearchDraft(search, draft, anchorSampleLabel) {
    return "".concat(getUICompanyName(draft, anchorSampleLabel), " ").concat(draft.title).toLowerCase().includes(search);
}
export function filterAndSortDrafts(data, search, sort, anchorSampleLabel) {
    const filtered = data ? data.filter((payout)=>{
        const normalizedSearch = search ? search.trim().toLowerCase() : search;
        const passesSearch = !normalizedSearch || textSearchDraft(normalizedSearch, payout, anchorSampleLabel);
        return passesSearch;
    }) : [];
    if (sort) {
        filtered.sort(sortFunctions[sort.colIndex](sort.sort === 'asc'));
    }
    return {
        drafts: filtered,
        draftsMap: new Map(filtered.map((draft)=>[
                draft.id,
                draft
            ]))
    };
}
export function bulkExternallySignErrorText(draft, error, defaultAgreementTitle, errorTexts, draftTexts) {
    const { text } = new ErrorResponse(error).createRelationshipErrorText(errorTexts);
    if (!draft) return null;
    const prefix = draft.contactDetails ? "".concat(draft.contactDetails.companyName, " (").concat(draft.contactDetails.email, ")") : draft.title || defaultAgreementTitle;
    return draftTexts.bulkErrorFormat({
        prefix,
        error: text
    });
}
// WHY: a chip-edited filter has no faithful NL (lastQuery describes only the original search), so a stale query would scope the bulk-ask "authoritative" count over pre-edit drafts — omit it once edited.
export function deriveActiveFilterText(lastQuery, filterEditedSinceLastSearch) {
    return filterEditedSinceLastSearch ? undefined : lastQuery || undefined;
}
